import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useLanguages, useInvalidate } from "@/lib/queries";
import { POS } from "@/components/language/WordDialog";

export function LanguagePicker({ uid, value, onChange }) {
  const { data: langs = [] } = useLanguages(uid);
  if (!langs.length) return <p className="text-sm text-[#A8A090]" data-testid="send-no-languages">No languages yet. Create one in the Languages section first.</p>;
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid="send-language-select"><SelectValue placeholder="Choose a language" /></SelectTrigger>
      <SelectContent>{langs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} · {l.word_count} words</SelectItem>)}</SelectContent>
    </Select>
  );
}

const pron = (ent) => Object.entries(ent.attributes || {}).find(([k, v]) => k.toLowerCase().includes("pronunc") && String(v).trim())?.[1] || "";

export function SendToLanguageDialog({ ent, onOpenChange }) {
  const invalidate = useInvalidate();
  const [lid, setLid] = useState("");
  const [form, setForm] = useState({ word: ent.name, pronunciation: pron(ent), meaning: ent.summary || "", part_of_speech: "", example: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/languages/${lid}/words`, { ...form, entity_id: ent.id });
      invalidate(["entity-words", ent.id], ["words", lid], ["language", lid], ["languages", ent.universe_id]);
      toast.success(`${form.word} added to the dictionary`);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="send-to-language-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Send to a language</DialogTitle>
            <DialogDescription>Add “{ent.name}” as a dictionary word. It stays linked to this entry.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-5">
            <div className="space-y-2"><Label>Language</Label><LanguagePicker uid={ent.universe_id} value={lid} onChange={setLid} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Word</Label><Input value={form.word} onChange={set("word")} required data-testid="send-word" /></div>
              <div className="space-y-2"><Label>Pronunciation</Label><Input value={form.pronunciation} onChange={set("pronunciation")} data-testid="send-pronunciation" /></div>
            </div>
            <div className="grid grid-cols-[1fr_160px] gap-3">
              <div className="space-y-2"><Label>Meaning</Label><Input value={form.meaning} onChange={set("meaning")} data-testid="send-meaning" /></div>
              <div className="space-y-2"><Label>Part of speech</Label>
                <Select value={form.part_of_speech || "none"} onValueChange={(v) => set("part_of_speech")(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="send-pos"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{POS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Example sentence</Label><Input value={form.example} onChange={set("example")} data-testid="send-example" /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="send-notes" /></div>
          </div>
          <DialogFooter><Button type="submit" disabled={busy || !lid || !form.word.trim()} data-testid="send-save-button">{busy ? "Adding…" : "Add to dictionary"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
